import { AsyncPipe } from "@angular/common";
import { Component, DestroyRef, inject, input, type OnInit, output } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { AlertService, Button, ButtonIcon, ProgressSpinner, Select } from "@ui";
import { finalize, type Observable, of, switchMap } from "rxjs";
import { DataGridComponent } from "../../data-grid/data-grid.component";
import { DefaultGridDataService } from "../../data-grid/default-grid-data.service";
import type { Corpus } from "@pienso/ingest-types/corpus.interface";
import type { ElasticFieldProps } from "@pienso/ingest-types/elastic.interface";
import type { Mapping, Pif } from "@pienso/ingest-types/pif.interface";
import { CorpusDataService } from "../../services/corpus-data.service";
import { ElasticSearchService } from "../../services/elastic-search.service";
import { PifDataService } from "../../services/pif-data.service";
import { MappingComponent } from "../../training-data/mapping/mapping.component";
import { type ContentColumn, TrainingDataService } from "../../training-data/training-data.service";
import { TrainingDataGridRendererService } from "../../training-data/training-data-grid-renderer.service";

// Format view for the ingest modal: replaces the mapping portion of the old routed TrainingDataComponent.
@Component({
  selector: "app-ingest-modal-format",
  templateUrl: "./format.component.html",
  styleUrls: ["./format.component.scss"],
  imports: [FormsModule, AsyncPipe, Button, ButtonIcon, ProgressSpinner, Select, DataGridComponent, MappingComponent],
})
export class IngestModalFormatComponent implements OnInit {
  private corpusService = inject(CorpusDataService);
  private elastic = inject(ElasticSearchService);
  private pifService = inject(PifDataService);
  private alertService = inject(AlertService).withDestroyRef(inject(DestroyRef));
  protected gridDataService = inject(DefaultGridDataService);
  protected gridRenderer = inject(TrainingDataGridRendererService);
  protected service = inject(TrainingDataService);

  readonly corpusId = input.required<number>();
  readonly pifId = input<number>();

  // emits the saved pif id, so the parent can move to the action step
  readonly formatted = output<number>();
  // emitted when the user wants to return to the previous ingest modal step
  readonly back = output<void>();

  corpus: Corpus | null = null;
  pif: Pif | null = null; // null if creating a new one

  columnProps: ElasticFieldProps | null = null;

  textCols: { colId: string; name: string }[] | null = null;

  isDirty = false;
  isSaving = false;

  ngOnInit() {
    this.corpus = null;
    this.pif = null;
    this.columnProps = null;
    this.textCols = null;
    this.isDirty = true;
    this.isSaving = false;
    this.service.reset();

    const pifId = this.pifId();

    this.corpusService
      .getById(this.corpusId())
      .pipe(
        switchMap((corpus: Corpus) => {
          this.corpus = corpus;
          return this.elastic.getColumnProperties(corpus.esIndex);
        }),
        switchMap((columnProps: ElasticFieldProps) => {
          this.columnProps = columnProps;
          this.textCols = this.corpus!.columnMeta.columnOrder.filter(
            (colId: string) => columnProps[colId].type === "text",
          ).map((colId: string) => ({ colId, name: this.corpus!.columnMeta.columns[colId].name }));

          if (pifId) {
            return this.pifService.getById(pifId) as Observable<Pif | null>;
          }
          return this.pifService.defaultMapping(this.corpusId()).pipe(
            switchMap((mapping) => {
              this.service.mapping = mapping;
              return of<Pif | null>(null);
            }),
          );
        }),
      )
      .subscribe({
        next: (pif: Pif | null) => this.updateFromPif(pif),
        error: (e) => this.alertService.error(e),
      });
  }

  updateFromPif(pif: Pif | null) {
    this.pif = pif;
    if (pif && pif.contentCol) {
      this.service.contentCol = { colId: pif.contentCol, name: this.corpus!.columnMeta.columns[pif.contentCol].name };
      this.service.mapping = pif.mapping;
    } else {
      this.service.contentCol = null;
    }
    this.gridDataService.setCorpus(this.corpus!);
    this.isDirty = false;
  }

  setContentCol(col: ContentColumn | null) {
    this.service.contentCol = col;
    this.isDirty = true;
  }

  mappingChanged(mapping: Mapping) {
    this.service.mapping = mapping;
    this.isDirty = true;
  }

  canSave(): boolean {
    return !!this.service.contentCol;
  }

  pifName() {
    return this.corpus ? this.corpus.corpusName + "-" + (this.service.contentCol?.name?.trim() ?? "") : "";
  }

  save(): void {
    if (!this.isDirty) {
      this.formatted.emit(this.pif!.id);
      return;
    }
    const contentCol = this.service.contentCol;
    const mapping = this.service.mapping;
    if (!contentCol || !mapping) {
      return;
    }
    this.isSaving = true;
    const request$ = this.pif?.id
      ? this.pifService.update(this.pif.id, { pifName: this.pifName(), contentCol: contentCol.colId, mapping })
      : this.pifService.create(this.corpusId(), {
          corpusId: this.corpusId(),
          pifName: this.pifName(),
          contentCol: contentCol.colId,
          mapping,
        });

    request$
      .pipe(finalize(() => (this.isSaving = false)))
      .subscribe({
        next: (pif: Pif) => {
          this.updateFromPif(pif);
          this.formatted.emit(pif.id);
        },
        error: (e) => this.alertService.error(e),
      });
  }
}
